import { cn } from "@u/cn";
import Button from "@ui/button";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DashboardFilterConfig } from "./dashboardData";
import { activeFilterChips, type ActiveFilterChip } from "./activeFilterChips";

export interface DashboardActiveFiltersProps {
  /**
   * Config-driven mode: pass the filter config + selected map and chips are
   * resolved automatically (DS collection surfaces). Ignored when `chips` is
   * given.
   */
  filters?: DashboardFilterConfig[];
  selected?: Record<string, string[]>;
  /**
   * Pre-built mode: pass chips directly. For typed/bespoke filter state that
   * isn't shaped as `Record<string, string[]>` (e.g. useToursFilters,
   * PortfolioVenues). Takes precedence over `filters`/`selected`.
   */
  chips?: ActiveFilterChip[];
  /** Remove a single value from a field. */
  onRemove(field: string, value: string): void;
  /** Clear every active filter. */
  onClear(): void;
  /** Hide the "Clear all" control (e.g. when the toolbar owns it). */
  hideClear?: boolean;
  className?: string;
}

/**
 * Active Filters bar — one removable chip per applied filter value + clear-all.
 * Renders nothing when no filter is active. Canonical replacement for the
 * hand-rolled "active count badge + clear" blocks across search surfaces
 * (Search Experience pattern §2.3 / §21).
 */
export function DashboardActiveFilters({
  filters,
  selected,
  chips,
  onRemove,
  onClear,
  hideClear,
  className,
}: DashboardActiveFiltersProps) {
  const { t } = useTranslation();
  const resolved = chips ?? activeFilterChips(filters ?? [], selected ?? {});
  if (resolved.length === 0) return null;

  return (
    <div
      className={cn("flex flex-wrap items-center gap-2", className)}
      role="group"
      aria-label={t("ds_active_filters", { defaultValue: "Active filters" })}
    >
      {resolved.map((chip) => (
        <button
          key={`${chip.field}:${chip.value}`}
          type="button"
          onClick={() => onRemove(chip.field, chip.value)}
          aria-label={t("ds_remove_filter", {
            field: chip.fieldLabel,
            value: chip.valueLabel,
            defaultValue: "Remove filter {{field}}: {{value}}",
          })}
          className="group inline-flex items-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs text-primary transition-colors hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
        >
          <span className="font-mono uppercase tracking-wider opacity-60">
            {chip.fieldLabel}
          </span>
          <span className="font-medium">{chip.valueLabel}</span>
          <X size={12} className="opacity-60 group-hover:opacity-100" />
        </button>
      ))}

      {!hideClear && (
        <Button
          design="ghost"
          size="sm"
          label={t("ds_clear_filters", { defaultValue: "Clear" })}
          onClick={onClear}
          className="h-7 text-xs text-muted-foreground hover:text-destructive"
        />
      )}
    </div>
  );
}
